/**
 * Review Screen Component
 * Post-game SRS review session
 */

import React from 'react';
import type { SRSRating, Question, SRSCard } from '../../../types/question';
import type { AnsweredQuestionRef } from '../../../types/game';
import { getQuestionById } from '../../../data/questions';
import { getIntervalDescription } from '../../../engine/srs';
import styles from './ReviewScreen.module.css';

interface ReviewScreenProps {
  // Game results
  xp: number;
  correctCount: number;
  totalQuestions: number;
  bestStreak: number;
  matchTime: string;
  playerWon: boolean;
  
  // Answered questions for review
  answeredQuestions: AnsweredQuestionRef[];
  
  // SRS data
  dueCards: SRSCard[];
  
  // SRS actions
  onRateQuestion: (questionId: string, rating: SRSRating) => void;
  
  // Navigation
  onPlayAgain: () => void;
  onShare: () => void;
  onTracker: () => void;
  
  // Labels
  labels: {
    youWin: string;
    youLose: string;
    botDurak: string;
    youDurak: string;
    xpEarned: string;
    correct: string;
    streak: string;
    time: string;
    review: string;
    playAgain: string;
    share: string;
    tracker: string;
    eraTitle: string;
    again: string;
    hard: string;
    good: string;
    easy: string;
    nextReview: string;
    mastered: string;
    reviewNeeded: string;
    eraSegments: {
      medieval: string;
      earlyModern: string;
      modern: string;
    };
    due: {
      title: string;
      count: (count: number) => string;
      reps: (count: number) => string;
      more: (count: number) => string;
    };
    notice: {
      due: (count: number) => string;
      caughtUp: string;
    };
    aria: {
      session: string;
      stats: string;
      byEra: string;
      eraDistribution: string;
      dueCards: string;
      rateRecall: string;
      srsSection: string;
      playAgain: string;
      share: string;
      viewTracker: string;
    };
  };
}

export const ReviewScreen: React.FC<ReviewScreenProps> = ({
  xp,
  correctCount,
  totalQuestions,
  bestStreak,
  matchTime,
  playerWon,
  answeredQuestions,
  dueCards,
  onRateQuestion,
  onPlayAgain,
  onShare,
  onTracker,
  labels,
}) => {
  // Helper to get full question data
  const getQuestion = (id: string): Question | undefined => {
    return getQuestionById(id);
  };

  return (
    <section
      id="screen-review"
      className={`screen ${styles.screen} active`}
      role="main"
      aria-label={labels.aria.session}
    >
      <div className={styles.reviewHeader}>
        <div style={{ fontSize: '2rem' }}>{playerWon ? '🏆' : '😔'}</div>
        <h1>{playerWon ? labels.youWin : labels.youLose}</h1>
        <p>{playerWon ? labels.botDurak : labels.youDurak}</p>
      </div>

      <div className={styles.scoreSummary} role="region" aria-label={labels.aria.stats}>
        <div className={styles.scoreBox}>
          <div className={styles.scoreVal}>{xp}</div>
          <div className={styles.scoreLbl}>{labels.xpEarned}</div>
        </div>
        <div className={styles.scoreBox}>
          <div className={styles.scoreVal}>{correctCount}/{totalQuestions}</div>
          <div className={styles.scoreLbl}>{labels.correct}</div>
        </div>
        <div className={styles.scoreBox}>
          <div className={styles.scoreVal}>🔥×{bestStreak}</div>
          <div className={styles.scoreLbl}>{labels.streak}</div>
        </div>
        <div className={styles.scoreBox}>
          <div className={styles.scoreVal}>{matchTime}</div>
          <div className={styles.scoreLbl}>{labels.time}</div>
        </div>
      </div>

      {/* Era histogram */}
      <div className={styles.histogramSection} role="region" aria-label={labels.aria.byEra}>
        <h3>{labels.eraTitle}</h3>
        <div className={styles.timelineBar} role="img" aria-label={labels.aria.eraDistribution}>
          <div className={`${styles.tlSeg} ${styles.tlSoviet}`} style={{ flexGrow: 3 }}>
            {labels.eraSegments.medieval}
          </div>
          <div className={`${styles.tlSeg} ${styles.tlEarly}`} style={{ flexGrow: 4 }}>
            {labels.eraSegments.earlyModern}
          </div>
          <div className={`${styles.tlSeg} ${styles.tlModern}`} style={{ flexGrow: 3 }}>
            {labels.eraSegments.modern}
          </div>
        </div>
      </div>

      {/* Due Cards Section */}
      {dueCards.length > 0 && (
        <div className={styles.dueSection} role="region" aria-label={labels.aria.dueCards}>
          <h3>{labels.due.title}</h3>
          <p className={styles.dueCount}>{labels.due.count(dueCards.length)}</p>
          <div className={styles.dueCards}>
            {dueCards.slice(0, 3).map((card) => {
              const fullQ = getQuestion(card.questionId);
              if (!fullQ) return null;
              return (
                <article key={card.questionId} className={styles.dueItem}>
                  <div className={styles.dueQ}>{fullQ.text}</div>
                  <div className={styles.dueMeta}>
                    <span className={styles.dueInterval}>⏰ {getIntervalDescription(card.interval)}</span>
                    <span className={styles.dueReps}>{labels.due.reps(card.repetitions)}</span>
                  </div>
                  <div className={styles.srsRatingRow} role="group" aria-label={labels.aria.rateRecall}>
                    <button
                      className={`${styles.srsRateBtn} ${styles.rateAgain}`}
                      onClick={() => onRateQuestion(card.questionId, 'again')}
                    >
                      {labels.again}
                    </button>
                    <button
                      className={`${styles.srsRateBtn} ${styles.rateHard}`}
                      onClick={() => onRateQuestion(card.questionId, 'hard')}
                    >
                      {labels.hard}
                    </button>
                    <button
                      className={`${styles.srsRateBtn} ${styles.rateGood}`}
                      onClick={() => onRateQuestion(card.questionId, 'good')}
                    >
                      {labels.good}
                    </button>
                    <button
                      className={`${styles.srsRateBtn} ${styles.rateEasy}`}
                      onClick={() => onRateQuestion(card.questionId, 'easy')}
                    >
                      {labels.easy}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
          {dueCards.length > 3 && (
            <p className={styles.moreDue}>{labels.due.more(dueCards.length - 3)}</p>
          )}
        </div>
      )}

      {/* SRS Review */}
      <div className={styles.srsSection} role="region" aria-label={labels.aria.srsSection}>
        <h3>{labels.review}</h3>
        <p className={styles.dueNotice} aria-live="polite">
          {dueCards.length > 0 ? labels.notice.due(dueCards.length) : labels.notice.caughtUp}
        </p>
        <div className={styles.srsCards}>
          {answeredQuestions.slice(-5).map((q) => {
            const fullQ = getQuestion(q.id);
            if (!fullQ) return null;
            return (
            <article
              key={q.id}
              className={`${styles.srsItem} ${q.wasCorrect ? styles.mastered : styles.reviewNeeded}`}
              aria-label={`${q.wasCorrect ? labels.mastered : labels.reviewNeeded}: ${fullQ.text}`}
            >
              <div className={styles.srsQ}>{fullQ.text}</div>
              <div className={styles.srsA}>{fullQ.feedback}</div>
              <div className={styles.srsPersp}>
                <span className={`${styles.srsPerspTag} ${styles.perspLt}`}>
                  🇱🇹 {fullQ.lt_view.substring(0, 30)}...
                </span>
                <span className={`${styles.srsPerspTag} ${styles.perspBe}`}>
                  🇧🇾 {fullQ.be_view.substring(0, 30)}...
                </span>
              </div>
              <div className={styles.srsCite}>📎 {fullQ.source}</div>
              <div className={styles.srsRatingRow} role="group" aria-label={labels.aria.rateRecall}>
                <button
                  className={`${styles.srsRateBtn} ${styles.rateAgain}`}
                  onClick={() => onRateQuestion(q.id, 'again')}
                >
                  {labels.again}
                </button>
                <button
                  className={`${styles.srsRateBtn} ${styles.rateHard}`}
                  onClick={() => onRateQuestion(q.id, 'hard')}
                >
                  {labels.hard}
                </button>
                <button
                  className={`${styles.srsRateBtn} ${styles.rateGood}`}
                  onClick={() => onRateQuestion(q.id, 'good')}
                >
                  {labels.good}
                </button>
                <button
                  className={`${styles.srsRateBtn} ${styles.rateEasy}`}
                  onClick={() => onRateQuestion(q.id, 'easy')}
                >
                  {labels.easy}
                </button>
              </div>
              <span className={styles.srsNextBadge}>⏰ {labels.nextReview}</span>
            </article>
          )})}
        </div>
      </div>

      <div className={styles.reviewFooter}>
        <button
          className={styles.btnPlayAgain}
          onClick={onPlayAgain}
          aria-label={labels.aria.playAgain}
        >
          {labels.playAgain}
        </button>
        <button
          className={styles.btnShare}
          onClick={onShare}
          aria-label={labels.aria.share}
        >
          {labels.share}
        </button>
        <button
          className={styles.btnTracker}
          onClick={onTracker}
          aria-label={labels.aria.viewTracker}
        >
          {labels.tracker}
        </button>
      </div>
    </section>
  );
};

export default ReviewScreen;
